/**
 * Repeat After Exercise (INT-CMP-001)
 * Play audio of a phrase, then have the user repeat it.
 * Voice mode is a self-assessment placeholder today (no recorder wired).
 */

import React, { useState, useCallback } from 'react';
import { TouchableOpacity } from 'react-native';
import { Box, Text, VStack, HStack } from '@gluestack-ui/themed';
import { FS_XS, FS_SM, FS_MD } from '../../theme';
import { useExerciseScoring } from '../../hooks/useExerciseScoring';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

interface RepeatAfterExerciseProps {
  prompt: string;
  targetPhrase: string;
  phoneticGuide?: string;
  hint?: string;
  voiceEnabled: boolean;
  onComplete: (correct: boolean, score: number) => void;
}

export const RepeatAfterExercise = React.memo(function RepeatAfterExercise({
  prompt,
  targetPhrase,
  phoneticGuide,
  hint,
  voiceEnabled,
  onComplete,
}: RepeatAfterExerciseProps) {
  const { theme: t, fontScale, ffs } = useDisplayTheme();

  const [showPhonetic, setShowPhonetic] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [score, setScore] = useState<number | null>(null);

  const handleSelfScore = useCallback((selfScore: number) => {
    setScore(selfScore);
    setCompleted(true);
    onComplete(selfScore >= 70, selfScore);
  }, [onComplete]);

  // Keyboard shortcuts: 1/2/3 keys for self-scoring (web only, no-op on native)
  const showScoring = !completed && !voiceEnabled;
  useExerciseScoring([40, 70, 95], handleSelfScore, showScoring);

  return (
    <Box
      p="$4"
      rounded="$lg"
      style={{ backgroundColor: t.card, borderWidth: 1, borderColor: t.border }}
    >
      <VStack space="md">
        {/* Prompt */}
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: t.text, ...ffs }}
        >
          {prompt}
        </Text>

        {/* Target phrase */}
        <Box
          p="$4"
          rounded="$lg"
          alignItems="center"
          style={{ backgroundColor: t.primarySoft }}
        >
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_MD * fontScale, fontWeight: '700', color: t.primary, textAlign: 'center', ...ffs }}
          >
            {targetPhrase}
          </Text>

          {phoneticGuide && showPhonetic && (
            <Text
              allowFontScaling={false}
              mt="$1"
              style={{ fontSize: FS_SM * fontScale, color: t.textSecondary, fontStyle: 'italic', ...ffs }}
            >
              /{phoneticGuide}/
            </Text>
          )}
        </Box>

        {/* Phonetic toggle */}
        {phoneticGuide && !showPhonetic && (
          <TouchableOpacity
            testID="repeat-show-phonetic"
            accessibilityLabel="Show pronunciation guide"
            accessibilityRole="button"
            onPress={() => setShowPhonetic(true)}
            activeOpacity={0.7}
          >
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_XS * fontScale, color: t.primary, textAlign: 'center', ...ffs }}
            >
              Show pronunciation guide
            </Text>
          </TouchableOpacity>
        )}

        {hint && (
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: t.textSecondary, fontStyle: 'italic', ...ffs }}
          >
            {hint}
          </Text>
        )}

        {/* Voice recording or self-assessment */}
        {!completed && !voiceEnabled && (
          <VStack space="sm">
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_XS * fontScale, color: t.textSecondary, textAlign: 'center', ...ffs }}
            >
              Practice saying the phrase above, then rate yourself:
            </Text>
            <HStack space="sm" justifyContent="center">
              {[
                { label: 'Needs work', score: 40, color: '#DC2626' },
                { label: 'Getting there', score: 70, color: '#D97706' },
                { label: 'Nailed it!', score: 95, color: '#059669' },
              ].map((option) => (
                <TouchableOpacity
                  key={option.label}
                  testID={`repeat-self-score-${option.score}`}
                  accessibilityLabel={option.label}
                  accessibilityRole="button"
                  onPress={() => handleSelfScore(option.score)}
                  activeOpacity={0.7}
                >
                  <Box
                    px="$3"
                    py="$2"
                    rounded="$md"
                    style={{ backgroundColor: t.isDark ? '#374151' : '#F3F4F6', borderWidth: 1, borderColor: t.border }}
                  >
                    <Text
                      allowFontScaling={false}
                      style={{ fontSize: FS_XS * fontScale, fontWeight: '600', color: option.color, ...ffs }}
                    >
                      {option.label}
                    </Text>
                  </Box>
                </TouchableOpacity>
              ))}
            </HStack>
          </VStack>
        )}

        {/* Voice enabled placeholder — no recorder integration yet (self-assessment only) */}
        {!completed && voiceEnabled && (
          <Box py="$3" alignItems="center">
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_SM * fontScale, color: t.textSecondary, textAlign: 'center', ...ffs }}
            >
              Tap the microphone to record your pronunciation
            </Text>
          </Box>
        )}

        {/* Result */}
        {completed && score !== null && (
          <Box
            p="$2"
            rounded="$md"
            style={{ backgroundColor: score >= 70 ? '#D1FAE5' : '#FEF3C7' }}
          >
            <Text
              allowFontScaling={false}
              style={{
                fontSize: FS_SM * fontScale,
                fontWeight: '600',
                color: score >= 70 ? '#065F46' : '#92400E',
                textAlign: 'center',
                ...ffs,
              }}
            >
              {score >= 70 ? '✓ Great job!' : 'Keep practicing!'}
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
});
